import { keyGenerator } from '@ctn/ui/components/ctn-utils';
import { type FileUpload } from '@ctn/ui/types/ctn-upload';
import { format } from 'date-fns';

import { FORMAT_DATE } from '@/constants/date.constants';
import type {
	FormValues,
	IDocumento,
	IDocumentoForm,
	IDocumentoFormGroup,
	ISujetoGroup,
	ISujetoListado,
	ITramite,
} from '@/interfaces/model/don.interface';
import {
	type ExtensionesPermitidas,
	type EntityConfig,
	type IMaestro,
	type TipoDocumento,
	type TipoIntervencion,
} from '@/interfaces/model/maestros.interface';
import { type IUserAuth } from '@/interfaces/model/user.interface';
import { getExtension } from '@/utils/file.utils';

import { getLookup } from './dropdown';

export const defaultValues = () => ({
	documentosRequeridos: [],
	documentosAdicionales: [],
	sujetosAdicionales: [],
	sujetosRequeridos: [],
	politicaAceptada: [],
	numeroTramite: '',
});

/**
 * Formatea un archivo para el estado de la aplicación.
 * * NOTA SOBRE FIABILIDAD: Se utiliza la extensión del archivo en lugar de 'rawFile.type'
 * para determinar el MIME type. Los navegadores dependen del registro del SO local y
 * fallan (devuelven "") con formatos técnicos específicos como .gml, .shp o .dwg.
 * Al cruzar la extensión extraída con nuestro maestro de 'extensionesPermitidas',
 * garantizamos una identificación consistente independientemente del entorno del usuario.
 */
export const formatDoc = ({
	file: { rawFile, returnBase64 },
	tipoDocumentoMaestro,
	idTipoDocumento,
}: {
	file: FileUpload;
	tipoDocumentoMaestro: IMaestro[];
	idTipoDocumento: number;
}): IDocumentoForm => {
	const maestroEncontrado: TipoDocumento | undefined = getLookup(
		idTipoDocumento,
		tipoDocumentoMaestro,
	) as TipoDocumento;
	const tipoDocumento: TipoDocumento =
		maestroEncontrado ??
		({
			id: idTipoDocumento,
			descripcion: 'Documento adjunto',
			extensionesPermitidas: [],
			minimoObligatorio: 0,
		} as any);
	const fileExtension: string = getExtension(rawFile.name).toLowerCase();
	let mimeTypeEncontrado: ExtensionesPermitidas | undefined = tipoDocumento.extensionesPermitidas?.find(
		({ extension }) => fileExtension === extension.toLowerCase(),
	);

	if (!mimeTypeEncontrado) {
		mimeTypeEncontrado = tipoDocumento.extensionesPermitidas?.find(
			({ extension }) => extension.toLowerCase() === 'pdf',
		);
	}

	const mimeType: ExtensionesPermitidas = mimeTypeEncontrado ?? {
		extension: 'pdf',
		nombre: 'application/pdf',
		icono: 'pdfDuotone' as any,
		visualizableVisor: true,
	};

	const documentoForm: IDocumentoForm = {
		nombre: rawFile.name,
		idReact: keyGenerator(),
		rawFile,
		tipoDocumento,
		contenidoBase64: returnBase64.replace(/^data:.+;base64,/, ''),
		mimeType,
		tamano: rawFile.size,
	};

	return documentoForm;
};

export const getAllEntitiesVigentes = <T extends EntityConfig>(object: T[]) => object.filter(({ vigente }) => vigente);

/**
 * Obtiene los tipos de documentos que se consideran obligatorios (minimoObligatorio mayor a 0).
 * Indicar solamente documentos/sujetos vigentes cuando se crea un nuevo borrador.
 */
export const getAllEntitiesRequireds = <T extends EntityConfig>(typeEntity: T[], onlyVigente?: boolean) => {
	let filtered = typeEntity.filter(({ minimoObligatorio }) => minimoObligatorio > 0);
	if (onlyVigente) {
		filtered = getAllEntitiesVigentes([...filtered]);
	}
	return filtered;
};

/**
 * Obtiene los tipos de documentos/sujetos que se consideran adicionales (minimoObligatorio igual a 0).
 * Indicar solamente documentos/sujetos vigentes cuando se crea un nuevo borrador.
 */
export const getAllEntitiesAdicionals = <T extends EntityConfig>(typeEntity: T[], onlyVigente?: boolean) => {
	let filtered = typeEntity.filter(({ minimoObligatorio }) => minimoObligatorio === 0);
	if (onlyVigente) {
		filtered = getAllEntitiesVigentes([...filtered]);
	}
	return filtered;
};

export const filterDocumentosAdicionales = (
	documentos: IDocumento[],
	tipoDocumentos: TipoDocumento[],
	onlyVigente?: boolean,
) => {
	const idTipoDocumentosAdicionales = getAllEntitiesAdicionals(tipoDocumentos, onlyVigente).map(({ id }) => id);
	const documentosAdicionales = getAllEntitiesAdicionals(tipoDocumentos, onlyVigente);	

	const documentosFinal = documentos
		.map((documento) => {
			const { tipoDocumento } = documento;
			const vigente = documentosAdicionales.find((doc)=> doc.id === tipoDocumento.id)?.vigente;
			return {
				...documento,
				tipoDocumento:{
					...tipoDocumento,
					vigente,
					adicional: true
				}
			};
		})
		.filter(({ tipoDocumento }) => idTipoDocumentosAdicionales.includes(tipoDocumento.id));
	return documentosFinal;
};

export const filterDocumentosRequeridos = (
	documentos: IDocumento[],
	tipoDocumentos: TipoDocumento[],
	onlyVigente?: boolean,
) => {
	const idTipoDocumentosFiltered = getAllEntitiesRequireds(tipoDocumentos, onlyVigente).map(({ id }) => id);
	return documentos.filter(({ tipoDocumento }) => idTipoDocumentosFiltered.includes(tipoDocumento.id));
};

export const mapFormToDB = ({
	form,
	user,
	id,
}: {
	form: FormValues;
	user: IUserAuth;
	id: number | undefined;
}): ITramite => {
	const {
		numeroTramite,
		fechaVencimiento,
		documentosAdicionales,
		documentosRequeridos,
		sujetosRequeridos,
		sujetosAdicionales,
		...values
	} = form;
	return {
		id,
		nifEmisor: user.nifEmisor ?? '',
		tipoSector: user.tipoSectorEmisor,
		preSeleccionNotario: form.notarioSeleccionado
			? {
				codigoCatastro: form.notarioSeleccionado.split(';')[0],
				cuv: form.notarioSeleccionado.split(';')[1],
			}
			: undefined,
		tramiteTMU: {
			...values,
			firmaTelematica: form.firmaTelematica,
			numeroTramite,
			fechaVencimiento: fechaVencimiento ? format(fechaVencimiento, FORMAT_DATE.FRONT) : undefined,
			sujetos: [...flattenGroupSujetos(sujetosRequeridos), ...sujetosAdicionales],
			documentos: [...flattenGroupDocumentos(documentosRequeridos), ...documentosAdicionales],
			solicitudesDocumentacion: [],
			historial: [],
		},
	};
};

export const filterSujetosAdicionales = (
	sujetos: ISujetoListado[],
	tipoIntervencion: TipoIntervencion[],
	onlyVigente?: boolean,
) => {
	const idTipoSujetoFiltered = getAllEntitiesAdicionals(tipoIntervencion, onlyVigente).map(({ id }) => id);
	return sujetos.filter(({ tipoIntervencion }) => idTipoSujetoFiltered.includes(tipoIntervencion.id));
};

export const filterSujetosRequeridos = (
	sujetos: ISujetoListado[],
	tipoIntervencion: TipoIntervencion[],
	onlyVigente?: boolean,
) => {
	const idTipoSujetoFiltered = getAllEntitiesRequireds(tipoIntervencion, onlyVigente).map(({ id }) => id);
	return sujetos.filter(({ tipoIntervencion }) => idTipoSujetoFiltered.includes(tipoIntervencion.id));
};

/**
 * Agrupa los sujetos en una estructura organizada por tipo de intervencion.
 */
export const mapGroupSujetos = (documentos: ISujetoListado[]): ISujetoGroup[] =>
	documentos.reduce<ISujetoGroup[]>((acc, curr) => {
		const { tipoIntervencion } = curr;
		const existingGroup = acc.find((g) => g.tipoIntervencion.id === tipoIntervencion?.id);

		if (existingGroup) {
			existingGroup.items.push(curr);
		} else {
			acc.push({
				tipoIntervencion,
				items: [curr],
			});
		}
		return acc;
	}, []);

/**
 * Revierte la agrupación de sujetos (mapGroupSujetos), transformando los grupos de nuevo en una lista plana de sujetos.
 */
export const flattenGroupSujetos = (grupos: ISujetoGroup[]): ISujetoListado[] => grupos.flatMap((grupo) => grupo.items);

/**
 * Agrupa los documentos en una estructura organizada por tipo de documento.
 */
export const mapGroupDocumentos = (documentos: IDocumentoForm[]): IDocumentoFormGroup[] =>
	documentos.reduce<IDocumentoFormGroup[]>((acc, curr) => {
		const { tipoDocumento } = curr;
		const existingGroup = acc.find((g) => g.tipoDocumento.id === tipoDocumento?.id);

		if (existingGroup) {
			existingGroup.items.push(curr);
		} else {
			acc.push({
				tipoDocumento,
				items: [curr],
			});
		}
		return acc;
	}, []);

/**
 * Revierte la agrupación de documentos (mapGroupDocumentos), transformando los grupos de nuevo en una lista plana de documentos.
 */
export const flattenGroupDocumentos = (grupos: IDocumentoFormGroup[]): IDocumentoForm[] =>
	grupos.flatMap((grupo) => grupo.items);

export const getAllDocumentForm = (data: FormValues): IDocumentoForm[] => {
	// 1. Extraemos los documentos planos (Adicionales)
	const docsAdicionales = data.documentosAdicionales ?? [];

	// 2. Extraemos y aplanamos los documentos agrupados (Requeridos)
	const docsRequeridos = data.documentosRequeridos.flatMap((grupo) => grupo.items) ?? [];

	// 3. Extraemos los documentos anidados profundamente (Representantes)
	const docsRepresentantesAdicionales =
		data.sujetosAdicionales
			.flatMap((sujeto) => sujeto.representantes?.map((rep) => rep.documentoPoder) ?? [])
			.filter((doc): doc is IDocumentoForm => Boolean(doc)) ?? [];

	const docsRepresentantesRequeridos =
		flattenGroupSujetos(data.sujetosRequeridos)
			.flatMap((sujeto) => sujeto.representantes?.map((rep) => rep.documentoPoder) ?? [])
			.filter((doc): doc is IDocumentoForm => Boolean(doc)) ?? [];

	return [...docsAdicionales, ...docsRequeridos, ...docsRepresentantesAdicionales, ...docsRepresentantesRequeridos];
};

export const getAllSujetos = (data: FormValues): ISujetoListado[] => [
	...data.sujetosAdicionales,
	...flattenGroupSujetos(data.sujetosRequeridos),
];
